// ===========================================
// Analytics Dashboard Page - Dev Costs View
// ===========================================
// Features:
// - Drag & drop section reordering (using @hello-pangea/dnd)
// - Layout persistence in localStorage
// - All portfolio metrics and cost analysis

import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../context/AuthContext';
import { analyticsAPI, authAPI } from '../services/api';
import { 
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, Layers, 
  Loader, RefreshCw, ArrowUpRight, Minus,
  Zap, Wallet, Share2, Bot, Activity,
  FileText, GripVertical, Lock, Unlock, Globe
} from 'lucide-react';
import TimePeriodFilter, { getDateRange } from '../components/TimePeriodFilter';
import './Analytics.scss';

// Color palette for charts
const COLORS = ['#00d4ff', '#10b981', '#a855f7', '#f59e0b', '#ec4899', '#3b82f6', '#ef4444', '#6b7280'];

// ===========================================
// Default Section Order
// ===========================================
// Layout: 2-column grid, items flow left-to-right, top-to-bottom
// Note: 'cost-inputs' removed - now in fixed "Cost Data Sources" section above
const DEFAULT_SECTION_ORDER = [
  'cost-breakdown',
  'cursor-charts',
  'portfolio-table'
];

// LocalStorage key for persisting section order
const SECTION_ORDER_KEY = 'devpad_analytics_section_order_v4';

const Analytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    overview: null,
    byProject: [],
    byServiceType: [],
    arrDistribution: []
  });
  
  // ===========================================
  // Section Order State (for drag & drop)
  // ===========================================
  const [sectionOrder, setSectionOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(SECTION_ORDER_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to load saved section order:', e);
    }
    return DEFAULT_SECTION_ORDER;
  });
  
  // Lock toggle to prevent accidental edits
  const [isLayoutLocked, setIsLayoutLocked] = useState(true);
  
  // ===========================================
  // Time Period Filter State
  // ===========================================
  const [timePeriod, setTimePeriod] = useState('past-12-months');

  // ===========================================
  // Cursor Usage Data State
  // ===========================================
  const [cursorUsage, setCursorUsage] = useState(null);
  const [cursorLoading, setCursorLoading] = useState(false);

  // ===========================================
  // Drag & Drop Handler
  // ===========================================
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(sectionOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setSectionOrder(items);
    
    // Persist to localStorage
    try {
      localStorage.setItem(SECTION_ORDER_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('Failed to save section order:', e);
    }
  };

  // Reset section order to default
  const resetLayout = () => {
    setSectionOrder(DEFAULT_SECTION_ORDER);
    localStorage.removeItem(SECTION_ORDER_KEY);
  };

  // Fetch Cursor usage data for the selected time period with trend comparison
  const fetchCursorUsage = useCallback(async () => {
    const range = getDateRange(timePeriod);
    
    setCursorLoading(true);
    try {
      const params = {
        includeTrends: true  // Request previous period comparison for trend indicators
      };
      if (range?.startDate) params.startDate = range.startDate.toISOString();
      if (range?.endDate) params.endDate = range.endDate.toISOString();

      const response = await authAPI.getCursorUsage(params);
      setCursorUsage(response.data.data.cursorUsage);
    } catch (error) {
      console.error('Failed to fetch cursor usage:', error);
      setCursorUsage(null);
    }
    setCursorLoading(false);
  }, [timePeriod]);

  // ===========================================
  // Fetch Analytics Data
  // ===========================================
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await analyticsAPI.getAll();
      const { overview, byProject, byServiceType, arrDistribution } = response.data.data;
      
      setData({
        overview,
        byProject: byProject || [],
        byServiceType: byServiceType || [],
        arrDistribution: arrDistribution || []
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  // ===========================================
  // Coordinated Initial Data Fetch
  // ===========================================
  // Fetch both analytics and cursor usage in parallel on mount
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      // Run both fetches in parallel for faster initial load
      await Promise.all([fetchAnalytics(), fetchCursorUsage()]);
      setLoading(false);
    };
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount
  
  // Refetch cursor usage when time period changes (after initial load)
  useEffect(() => {
    // Skip if this is the initial mount (loading is true initially)
    if (!loading) {
      fetchCursorUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod]); // Only when time period changes

  // ===========================================
  // Helper Functions
  // ===========================================
  
  // Format currency with USD symbol
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Format token count with consistent decimals
  const formatTokens = (tokens) => {
    if (!tokens) return '0 tokens';
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M tokens`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K tokens`;
    return `${tokens} tokens`;
  };

  // Returns project status with appropriate color coding
  // Status options: Idea, Active, Completed, Paused, Archived
  const getHealthIndicator = (project) => {
    const statusConfig = {
      idea: { color: '#a855f7', label: 'Idea' },        // Purple
      active: { color: '#22c55e', label: 'Active' },    // Green
      completed: { color: '#3b82f6', label: 'Completed' }, // Blue
      paused: { color: '#f59e0b', label: 'Paused' },    // Orange
      archived: { color: '#52525b', label: 'Archived' } // Gray
    };
    
    const status = project.status || 'idea';
    const config = statusConfig[status] || statusConfig.idea;
    return { color: config.color, label: config.label, icon: '●' };
  };

  // Get period info for investment calculations
  const getPeriodInfo = () => {
    const now = new Date();
    switch (timePeriod) {
      case 'this-week':
        return { months: 0.25, label: 'This Week' };
      case 'this-month':
        return { months: 1, label: 'This Month' };
      case 'last-month':
        return { months: 1, label: 'Last Month' };
      case 'last-3-months':
        return { months: 3, label: 'Last 3 Months' };
      case 'ytd':
        return { months: now.getMonth() + 1, label: 'Year to Date' };
      case 'past-12-months':
      default:
        return { months: 12, label: 'Past 12 Months' };
    }
  };

  const periodInfo = getPeriodInfo();

  // ===========================================
  // Calculate Actual Total Investment
  // ===========================================
  // Uses lifetime subscription spend (from startDate) + AI spend + infra
  const calculateTotalInvestment = () => {
    // 1. AI Token Spend - use actual Cursor data (time-filtered)
    const aiSpend = cursorUsage?.summary?.totalCost || 0;
    
    // 2. Fixed Subscriptions - use lifetime spend from backend (calculated from startDate)
    // This tracks total investment since each subscription started
    const subscriptionSpend = data.overview?.lifetimeSubscriptionSpend || 0;
    
    // 3. Project Infrastructure - current monthly costs * period months
    // (No historical data available, so we use current snapshot)
    const infraSpend = (data.overview?.totalMonthlyCost || 0) * periodInfo.months;
    
    return aiSpend + subscriptionSpend + infraSpend;
  };

  // ===========================================
  // Loading State
  // ===========================================
  if (loading) {
    return (
      <div className="analytics analytics--loading">
        <Loader className="animate-spin" size={32} />
        <p>Loading analytics...</p>
      </div>
    );
  }

  const { overview, byProject } = data;
  const totalGlobalCost = overview?.totalGlobalSubscriptionCost || 0;
  const sharedCostPerProject = overview?.sharedCostPerProject || 0;

  // ===========================================
  // Section Components Map
  // ===========================================
  // Sections - grid will place them in 2 columns by default
  const sections = {
    // ===========================================
    // Portfolio Table Section
    // ===========================================
    // Shows project costs with unaccounted AI spend delta at bottom
    'portfolio-table': {
      title: 'Application Portfolio - True Cost',
      icon: <TrendingUp size={16} />,
      // Side by side with cost-breakdown (no fullWidth)
      content: (() => {
        // Calculate total AI spend allocated to projects
        const totalAllocatedAISpend = byProject.reduce(
          (sum, project) => sum + (project.aiDevTokenSpend || 0), 0
        );
        // Total AI spend from Cursor usage data
        const totalAISpend = cursorUsage?.summary?.totalCost || 0;
        // Unaccounted delta = Total AI Spend - Allocated to projects
        const unaccountedAISpend = totalAISpend - totalAllocatedAISpend;
        
        return (
          <table className="analytics__table">
            <thead>
              <tr>
                <th>Project</th>
                <th>ARR</th>
                <th>AI Spend</th>
                <th>Infra</th>
                <th>+ Shared</th>
                <th>Total/mo</th>
                <th>True ROI</th>
                <th>Health</th>
              </tr>
            </thead>
            <tbody>
              {byProject.length > 0 ? (
                byProject.map((project) => {
                  const health = getHealthIndicator(project);
                  return (
                    <tr key={project._id || project.id}>
                      <td><span className="analytics__project-name">{project.name}</span></td>
                      <td><span className="analytics__arr">{project.arrLevel || '$0'}</span></td>
                      <td className="analytics__cost">${(project.aiDevTokenSpend || 0).toFixed(0)}</td>
                      <td className="analytics__cost">${(project.totalMonthlyCost || 0).toFixed(0)}</td>
                      <td className="analytics__cost analytics__cost--shared">+${(project.sharedCost || 0).toFixed(0)}</td>
                      <td className="analytics__cost analytics__cost--highlight">${(project.totalWithShared || project.totalMonthlyInvestment || 0).toFixed(0)}</td>
                      <td className="analytics__roi">{(project.roiWithShared || project.roi || 0).toFixed(1)}x</td>
                      <td>
                        <span className="analytics__health" style={{ color: health.color }}>
                          {health.icon} {health.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="analytics__table-empty">
                    No projects to display. Add projects with investment data to see your portfolio.
                  </td>
                </tr>
              )}
            </tbody>
            {/* Totals footer with unaccounted AI spend delta */}
            <tfoot>
              <tr className="analytics__table-totals">
                <td colSpan={2} className="analytics__totals-label">Unallocated</td>
                <td className={`analytics__cost analytics__cost--delta ${unaccountedAISpend > 0 ? 'analytics__cost--warning' : 'analytics__cost--balanced'}`}>
                  {unaccountedAISpend !== 0 ? (unaccountedAISpend > 0 ? '+' : '') : ''}${unaccountedAISpend.toFixed(0)}
                </td>
                <td colSpan={5}></td>
              </tr>
            </tfoot>
          </table>
        );
      })()
    },
    
    'cost-breakdown': {
      title: 'Total Cost Breakdown',
      icon: <DollarSign size={16} />,
      // Side by side with portfolio-table (no fullWidth)
      subtitle: `Total: ${formatCurrency(totalGlobalCost + (cursorUsage?.summary?.totalCost || 0) + (overview?.totalMonthlyCost || 0))}/mo`,
      content: (() => {
        const costItems = [];
        const totalAllCosts = totalGlobalCost + (cursorUsage?.summary?.totalCost || 0) + (overview?.totalMonthlyCost || 0);
        
        (user?.globalSubscriptions || []).filter(s => s.isActive).forEach(sub => {
          const monthlyCost = sub.billingCycle === 'yearly' ? sub.monthlyCost / 12 : sub.monthlyCost;
          costItems.push({ name: sub.name, category: 'subscription', cost: monthlyCost, color: '#3b82f6' });
        });
        
        if (cursorUsage?.summary?.totalCost > 0) {
          costItems.push({ name: 'Cursor AI Usage', category: 'ai', cost: cursorUsage.summary.totalCost, color: '#a855f7' });
        }
        
        if (overview?.totalMonthlyCost > 0) {
          costItems.push({ name: 'Project Infrastructure', category: 'infra', cost: overview.totalMonthlyCost, color: '#f59e0b' });
        }
        
        costItems.sort((a, b) => b.cost - a.cost);
        const maxCost = Math.max(...costItems.map(i => i.cost), 1);

        return (
          <div className="analytics__cost-bars">
            {costItems.length > 0 ? costItems.map((item, index) => (
              <div key={index} className="analytics__cost-bar-row">
                <div className="analytics__cost-bar-label">
                  <span className="analytics__cost-bar-name">{item.name}</span>
                  <span className="analytics__cost-bar-category">{item.category}</span>
                </div>
                <div className="analytics__cost-bar-track">
                  <div 
                    className="analytics__cost-bar-fill"
                    style={{ width: `${(item.cost / maxCost) * 100}%`, backgroundColor: item.color }}
                  />
                </div>
                <div className="analytics__cost-bar-value">
                  ${item.cost.toFixed(0)}
                  <span className="analytics__cost-bar-percent">
                    {totalAllCosts > 0 ? `${((item.cost / totalAllCosts) * 100).toFixed(0)}%` : ''}
                  </span>
                </div>
              </div>
            )) : (
              <div className="analytics__cost-bars-empty"><p>No costs recorded yet</p></div>
            )}
          </div>
        );
      })()
    },

    'cursor-charts': {
      title: 'Cursor AI Analytics',
      icon: <Bot size={16} />,
      content: cursorUsage?.summary ? (
        <div className="analytics__cursor-analytics">
          <div className="analytics__cursor-charts-row">
            {cursorUsage.byMonth?.length > 0 && (
              <div className="analytics__chart">
                <h4>Consumption Over Time</h4>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={cursorUsage.byMonth} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <XAxis dataKey="month" tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }} axisLine={{ stroke: 'var(--color-border)' }} tickLine={false} />
                    <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                    <Tooltip contentStyle={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 0, fontSize: 12 }} formatter={(v) => [`$${v.toFixed(2)}`, 'Cost']} />
                    <Bar dataKey="cost" fill="var(--color-accent)" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {cursorUsage.byModel?.length > 0 && (
              <div className="analytics__chart">
                <h4>Model Distribution</h4>
                <div className="analytics__pie-container">
                  <ResponsiveContainer width="100%" height={100}>
                    <PieChart>
                      <Pie data={cursorUsage.byModel.slice(0, 5)} dataKey="cost" nameKey="model" cx="50%" cy="50%" innerRadius={25} outerRadius={40} strokeWidth={1} stroke="var(--color-bg-tertiary)">
                        {cursorUsage.byModel.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 0, fontSize: 12 }} formatter={(v) => [`$${v.toFixed(2)}`, 'Cost']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="analytics__pie-legend">
                    {cursorUsage.byModel.slice(0, 5).map((item, index) => (
                      <div key={index} className="analytics__pie-legend-item">
                        <span className="analytics__pie-legend-dot" style={{ background: COLORS[index % COLORS.length] }} />
                        <span className="analytics__pie-legend-label">{item.model.replace('claude-', '').replace('-high-thinking', '')}</span>
                        <span className="analytics__pie-legend-value">${item.cost.toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Second row: Monthly Breakdown (under Consumption) | Cost by Model (under Distribution) */}
          <div className="analytics__cursor-charts-row">
            {cursorUsage.byMonth?.length > 0 && (
              <div className="analytics__chart analytics__chart--list">
                <h4>Monthly Breakdown</h4>
                <div className="analytics__month-list">
                  {cursorUsage.byMonth.slice(-5).reverse().map((item, i) => (
                    <div key={i} className="analytics__month-item">
                      <span className="analytics__month-name">{item.month}</span>
                      <div className="analytics__month-costs">
                        <span className="analytics__month-total">${item.cost.toFixed(2)}</span>
                        <span className="analytics__month-ondemand">${item.onDemandCost?.toFixed(2) || '0.00'} on-demand</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cursorUsage.byModel?.length > 0 && (
              <div className="analytics__chart analytics__chart--list">
                <h4>Cost by Model</h4>
                <div className="analytics__model-list">
                  {cursorUsage.byModel.slice(0, 5).map((item, i) => (
                    <div key={i} className="analytics__model-item">
                      <span className="analytics__model-rank">{i + 1}</span>
                      <span className="analytics__model-name">{item.model.replace('claude-', '').replace('-high-thinking', '')}</span>
                      <span className="analytics__model-cost">${item.cost.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="analytics__empty">
          <p>No Cursor AI data available for this period.</p>
          <p className="analytics__empty-hint">Upload billing CSV in Dev Profile</p>
        </div>
      )
    },

  };
  
  return (
    <div className="analytics">
      {/* ===========================================
          Header - Project Analytics View
          =========================================== */}
      <div className="analytics__header">
        <div>
          <h1>Dev Costs</h1>
          <p>Track your project investments, costs, and revenue</p>
        </div>
        <div className="analytics__header-actions">
          <button 
            className={`btn-icon ${isLayoutLocked ? '' : 'btn-icon--active'}`} 
            onClick={() => setIsLayoutLocked(!isLayoutLocked)}
            title={isLayoutLocked ? 'Unlock layout to reorder sections' : 'Lock layout'}
          >
            {isLayoutLocked ? <Lock size={16} /> : <Unlock size={16} />}
          </button>
          {!isLayoutLocked && (
            <button className="btn-secondary btn-sm" onClick={resetLayout}>
              Reset Order
            </button>
          )}
          <button className="btn-secondary" onClick={() => { fetchAnalytics(); fetchCursorUsage(); }}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Layout edit mode indicator */}
      {!isLayoutLocked && (
        <div className="analytics__edit-mode-banner">
          <GripVertical size={14} />
          <span>Edit Mode: Drag sections to reorder</span>
        </div>
      )}

      {/* ===========================================
          Global Time Period Filter
          =========================================== */}
      <div className="analytics__global-filter">
        <TimePeriodFilter selectedPeriod={timePeriod} onPeriodChange={setTimePeriod} />
      </div>

      {/* ===========================================
          Top KPI Cards
          =========================================== */}
      <div className="analytics__cards">
        <div className="analytics__card analytics__card--revenue">
          <div className="analytics__card-icon analytics__card-icon--green"><TrendingUp size={16} /></div>
          <div className="analytics__card-content">
            <span className="analytics__card-label">Portfolio ARR</span>
            <span className="analytics__card-value">{formatCurrency(overview?.totalARR || 0)}</span>
            <span className="analytics__card-change analytics__card-change--success"><ArrowUpRight size={14} /> {overview?.revenueGeneratingProjects || 0} revenue projects</span>
          </div>
        </div>

        <div className="analytics__card analytics__card--primary">
          <div className="analytics__card-icon"><Wallet size={16} /></div>
          <div className="analytics__card-content">
            <span className="analytics__card-label">Total Investment</span>
            {/* Lifetime subscription spend (from startDate) + AI spend + infra */}
            <span className="analytics__card-value">{formatCurrency(calculateTotalInvestment())}</span>
            <span className="analytics__card-change analytics__card-change--neutral"><Layers size={14} /> Lifetime Dev Spend</span>
          </div>
        </div>

        <div className="analytics__card">
          <div className="analytics__card-icon analytics__card-icon--blue"><Globe size={16} /></div>
          <div className="analytics__card-content">
            <span className="analytics__card-label">Fixed Subscriptions</span>
            <span className="analytics__card-value">{formatCurrency(totalGlobalCost)}/mo</span>
            <span className="analytics__card-change analytics__card-change--neutral"><Share2 size={14} /> ${sharedCostPerProject.toFixed(0)}/project</span>
          </div>
        </div>

        <div className="analytics__card analytics__card--ai">
          <div className="analytics__card-icon analytics__card-icon--purple"><Bot size={16} /></div>
          <div className="analytics__card-content">
            <span className="analytics__card-label">AI Token Spend</span>
            <span className="analytics__card-value">{cursorLoading ? '...' : `$${(cursorUsage?.summary?.totalCost || 0).toFixed(2)}`}</span>
            {cursorUsage?.trends ? (
              <span className={`analytics__card-change ${cursorUsage.trends.costChange > 0 ? 'analytics__card-change--negative' : cursorUsage.trends.costChange < 0 ? 'analytics__card-change--positive' : 'analytics__card-change--neutral'}`}>
                {cursorUsage.trends.costChange > 0 ? <TrendingUp size={12} /> : cursorUsage.trends.costChange < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                {cursorUsage.trends.costChange > 0 ? '+' : ''}{cursorUsage.trends.costChange}% vs prev
              </span>
            ) : (
              <span className="analytics__card-change analytics__card-change--neutral"><Zap size={14} /> ${(cursorUsage?.summary?.onDemandCost || 0).toFixed(2)} on-demand</span>
            )}
          </div>
        </div>

        <div className="analytics__card">
          <div className="analytics__card-icon"><FileText size={16} /></div>
          <div className="analytics__card-content">
            <span className="analytics__card-label">AI Requests</span>
            <span className="analytics__card-value">{cursorLoading ? '...' : (cursorUsage?.summary?.eventCount || 0).toLocaleString()}</span>
            {cursorUsage?.trends ? (
              <span className={`analytics__card-change ${cursorUsage.trends.eventChange > 0 ? 'analytics__card-change--success' : cursorUsage.trends.eventChange < 0 ? 'analytics__card-change--negative' : 'analytics__card-change--neutral'}`}>
                {cursorUsage.trends.eventChange > 0 ? <TrendingUp size={12} /> : cursorUsage.trends.eventChange < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                {cursorUsage.trends.eventChange > 0 ? '+' : ''}{cursorUsage.trends.eventChange}% vs prev
              </span>
            ) : (
              <span className="analytics__card-change analytics__card-change--neutral">
                <Activity size={14} />
                {formatTokens(cursorUsage?.summary?.totalTokens)}
              </span>
            )}
          </div>
        </div>

        <div className="analytics__card analytics__card--highlight">
          <div className="analytics__card-icon analytics__card-icon--gold"><Zap size={16} /></div>
          <div className="analytics__card-content">
            <span className="analytics__card-label">True Portfolio ROI</span>
            <span className="analytics__card-value">{overview?.portfolioROI || 0}x</span>
            <span className="analytics__card-change analytics__card-change--success">ARR / All Costs</span>
          </div>
        </div>
      </div>

      {/* ===========================================
          Draggable Sections
          =========================================== */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="analytics-sections" isDropDisabled={isLayoutLocked}>
          {(provided, snapshot) => (
            <div 
              className={`analytics__sections ${snapshot.isDraggingOver ? 'analytics__sections--dragging' : ''}`}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {sectionOrder.map((sectionId, index) => {
                const section = sections[sectionId];
                if (!section) return null;
                
                return (
                  <Draggable 
                    key={sectionId} 
                    draggableId={sectionId} 
                    index={index}
                    isDragDisabled={isLayoutLocked}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`analytics__section ${snapshot.isDragging ? 'analytics__section--dragging' : ''} ${section.fullWidth ? 'analytics__section--full-width' : ''}`}
                      >
                        <div 
                          className="analytics__section-header-bar"
                          {...provided.dragHandleProps}
                        >
                          {!isLayoutLocked && <GripVertical size={14} className="analytics__drag-handle" />}
                          {section.icon}
                          <h3>{section.title}</h3>
                          {section.subtitle && <span className="analytics__section-subtitle">{section.subtitle}</span>}
                        </div>
                        <div className="analytics__section-body">
                          {section.content}
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default Analytics;
